import { useState, useCallback } from 'react';

const clamp = (value, min, max) => {
  if (!max && max !== 0) return Math.max(value, min);
  return Math.min(Math.max(value, min), max);
};

const useResizablePanel = ({ initialWidth = 800, initialHeight = 0, minWidth = 320, maxWidth = 1280, minHeight = 0, maxHeight = 1024 } = {}) => {
  const [size, setSize] = useState({ width: initialWidth, height: initialHeight });

  const startResizing = useCallback(
    (event, direction = 'right') => {
      event.preventDefault();
      event.stopPropagation();

      const isPointerEvent = typeof event.pointerId === 'number';
      const moveEventName = isPointerEvent ? 'pointermove' : 'mousemove';
      const upEventName = isPointerEvent ? 'pointerup' : 'mouseup';

      const startX = event.clientX ?? (event.touches ? event.touches[0].clientX : 0);
      const startY = event.clientY ?? (event.touches ? event.touches[0].clientY : 0);
      const pointerId = isPointerEvent ? event.pointerId : null;

      const startWidth = size.width;
      const startHeight = size.height;

      const handleMove = (moveEvent) => {
        if (pointerId !== null && moveEvent.pointerId !== pointerId) return;
        moveEvent.preventDefault();
        const clientX = moveEvent.clientX ?? (moveEvent.touches ? moveEvent.touches[0].clientX : 0);
        const clientY = moveEvent.clientY ?? (moveEvent.touches ? moveEvent.touches[0].clientY : 0);
        const deltaX = clientX - startX;
        const deltaY = clientY - startY;

        setSize((prev) => {
          let nextWidth = prev.width;
          let nextHeight = prev.height;

          if (direction.includes('right')) {
            nextWidth = clamp(startWidth + deltaX, minWidth, maxWidth);
          } else if (direction.includes('left')) {
            nextWidth = clamp(startWidth - deltaX, minWidth, maxWidth);
          }

          if (direction.includes('bottom')) {
            nextHeight = clamp(startHeight + deltaY, minHeight, maxHeight);
          } else if (direction.includes('top')) {
            nextHeight = clamp(startHeight - deltaY, minHeight, maxHeight);
          }

          return { width: nextWidth, height: nextHeight };
        });
      };

      const handleUp = (upEvent) => {
        if (pointerId !== null && upEvent.pointerId !== pointerId) return;
        window.removeEventListener(moveEventName, handleMove);
        window.removeEventListener(upEventName, handleUp);
      };

      window.addEventListener(moveEventName, handleMove, { passive: false });
      window.addEventListener(upEventName, handleUp);
    },
    [size, minWidth, maxWidth, minHeight, maxHeight]
  );

  return { panelSize: size, setPanelSize: setSize, startResizing };
};

export default useResizablePanel;
